import { Offcanvas, Nav } from "react-bootstrap";
import styles from "../../styles/common/sidebar.module.css";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { DeleteTokenInIndexDB } from "../../utils/indexedDBUtils/delete-index-db";
import { GetTokenFromIndexedDB } from "../../utils/indexedDBUtils/get-indexed-db";
import IsTokenExpired from "../../utils/isTokenExpiredFunction/is-token-expired";
import { useSelector } from "react-redux";
import { userDetailProps } from "../../interfaces/user/detail";
import { SidebarProps } from "../../interfaces/common/funtions";
import {
    FiArrowLeft,
    FiChevronRight,
    FiCompass,
    FiGrid,
    FiHeart,
    FiHelpCircle,
    FiHome,
    FiInfo,
    FiLogIn,
    FiLogOut,
    FiSearch,
    FiSettings,
    FiShoppingBag,
    FiShoppingCart,
    FiStar,
    FiTag,
    FiTrendingUp,
    FiUser,
    FiUsers,
} from "react-icons/fi";
import { PRODUCT_CATEGORIES } from "../../data/productCategories";

type SidebarPanel = "main" | "categories" | "collections" | "account" | "help";

const Sidebar: React.FC<SidebarProps> = ({
    show,
    onHide,
    handleLogout,
    isAuthenticated,
    setIsAuthenticated,
}) => {
    const [activePanel, setActivePanel] = useState<SidebarPanel>("main");
    const [isUserDetails, setIsUserDetails] = useState<Partial<userDetailProps>>({});
    const navigate = useNavigate();
    const account = useSelector((state: any) => state.auth);

    const displayName = useMemo(() => {
        const name = isUserDetails.name?.trim();
        const username = isUserDetails.username?.trim();
        const emailName = isUserDetails.email?.split("@")[0]?.trim();

        return name || username || emailName || "User";
    }, [isUserDetails.name, isUserDetails.username, isUserDetails.email]);

    const userInitial = displayName.charAt(0).toUpperCase();

    const goTo = (path: string) => {
        navigate(path);
        onHide();
    };

    const openPanel = (panel: SidebarPanel) => {
        setActivePanel(panel);
    };

    const goBack = () => {
        setActivePanel("main");
    };

    useEffect(() => {
        const checkToken = async () => {
            const token = await GetTokenFromIndexedDB("authToken");

            if (!token) {
                setIsAuthenticated(false);
                setIsUserDetails({});
                return;
            }

            try {
                const decodedToken = jwtDecode<userDetailProps>(String(token));
                const isTokenValid = !IsTokenExpired(decodedToken);

                if (isTokenValid) {
                    setIsUserDetails(decodedToken);
                    setIsAuthenticated(true);
                } else {
                    await DeleteTokenInIndexDB("authToken");
                    setIsUserDetails({});
                    setIsAuthenticated(false);
                }
            } catch {
                setIsUserDetails({});
                setIsAuthenticated(false);
            }
        };

        checkToken();

        return () => undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account.statusCode]);

    useEffect(() => {
        if (!show) {
            setActivePanel("main");
        }
    }, [show]);

    const categoryItems = PRODUCT_CATEGORIES.map((category) => ({
        label: category.label,
        value: category.value,
        icon: category.icon,
    }));

    const collectionItems = [
        { label: "Diwali Deals", path: "/diwali-deals", icon: "🎆" },
        { label: "Best Sellers", path: "/best-sellers", icon: "🏆" },
        { label: "Trending", path: "/trending", icon: "🔥" },
        { label: "Best Rated", path: "/best-rated", icon: "⭐" },
        { label: "Featured Products", path: "/featured", icon: "✨" },
        { label: "Limited Offers", path: "/limited-offers", icon: "🏷️" },
        { label: "Latest Picks", path: "/latest-sellings", icon: "🆕" },
    ];

    const renderHeader = (title: string, subtitle?: string, back = false) => (
        <Offcanvas.Header className={styles.offcanvasHeader}>
            <div className={styles.headerContent}>
                {back && (
                    <button type="button" className={styles.backButton} onClick={goBack} aria-label="Back to main menu">
                        <FiArrowLeft />
                    </button>
                )}

                {isAuthenticated ? (
                    isUserDetails.profileImage ? (
                        <img
                            src={isUserDetails.profileImage}
                            alt="Profile"
                            className={styles.profileImage}
                        />
                    ) : (
                        <div className={styles.userInitial} aria-hidden="true">
                            {userInitial}
                        </div>
                    )
                ) : (
                    <div className={styles.guestIcon} aria-hidden="true">
                        <FiUser />
                    </div>
                )}

                <div className={styles.greetingBlock}>
                    <div className={styles.greeting}>
                        {isAuthenticated ? `Hello, ${displayName}` : "Hello, sign in"}
                    </div>
                    {subtitle && <div className={styles.panelSubtitle}>{subtitle}</div>}
                </div>

                <button type="button" className={styles.closeButton} onClick={onHide} aria-label="Close menu">
                    <AiOutlineCloseCircle />
                </button>
            </div>
        </Offcanvas.Header>
    );

    const renderMainMenu = () => (
        <>
            {renderHeader("Shopping")}

            <Offcanvas.Body className={styles.offcanvasBody}>
                <Nav className={styles.nav}>
                    <div className={styles.sectionLabel}>QUICK ACCESS</div>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/")}>
                        <span className={styles.linkIcon}><FiHome /></span>
                        <span>Home</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/products")}>
                        <span className={styles.linkIcon}><FiCompass /></span>
                        <span>All Products</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/search-result")}>
                        <span className={styles.linkIcon}><FiSearch /></span>
                        <span>Search Products</span>
                    </Nav.Link>

                    {isAuthenticated && (
                        <Nav.Link className={styles.navLink} onClick={() => goTo("/get-all-carts")}>
                            <span className={styles.linkIcon}><FiShoppingCart /></span>
                            <span>My Cart</span>
                        </Nav.Link>
                    )}

                    <div className={styles.navLinkLine} />

                    <div className={styles.sectionLabel}>DISCOVER</div>

                    <Nav.Link className={styles.navLink} onClick={() => openPanel("collections")}>
                        <span className={styles.linkIcon}><FiTrendingUp /></span>
                        <span>Deals & Collections</span>
                        <FiChevronRight className={styles.chevron} />
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => openPanel("categories")}>
                        <span className={styles.linkIcon}><FiGrid /></span>
                        <span>Shop by Category</span>
                        <FiChevronRight className={styles.chevron} />
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/products?audience=boys")}>
                        <span className={styles.linkIcon}><FiUsers /></span>
                        <span>Boys</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/products?audience=girls")}>
                        <span className={styles.linkIcon}><FiHeart /></span>
                        <span>Girls</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/products?audience=both")}>
                        <span className={styles.linkIcon}><FiUsers /></span>
                        <span>Boys & Girls</span>
                    </Nav.Link>

                    <div className={styles.navLinkLine} />

                    <div className={styles.sectionLabel}>POPULAR</div>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/mobiles")}>
                        <span className={styles.linkIcon}>📱</span>
                        <span>Mobiles & Electronics</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/beauty")}>
                        <span className={styles.linkIcon}>💄</span>
                        <span>Beauty</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/toys")}>
                        <span className={styles.linkIcon}>🧸</span>
                        <span>Toys & Kids</span>
                    </Nav.Link>

                    <div className={styles.navLinkLine} />

                    <div className={styles.sectionLabel}>ACCOUNT & HELP</div>

                    {isAuthenticated && (
                        <Nav.Link className={styles.navLink} onClick={() => goTo("/profile-setting")}>
                            <span className={styles.linkIcon}><FiSettings /></span>
                            <span>Profile & Settings</span>
                        </Nav.Link>
                    )}

                    <Nav.Link className={styles.navLink} onClick={() => openPanel("help")}>
                        <span className={styles.linkIcon}><FiHelpCircle /></span>
                        <span>Help & Information</span>
                        <FiChevronRight className={styles.chevron} />
                    </Nav.Link>

                    {isAuthenticated ? (
                        <Nav.Link className={`${styles.navLink} ${styles.dangerLink}`} onClick={() => {
                            onHide();
                            handleLogout();
                        }}>
                            <span className={styles.linkIcon}><FiLogOut /></span>
                            <span>Sign Out</span>
                        </Nav.Link>
                    ) : (
                        <Nav.Link className={styles.navLink} onClick={() => goTo("/auth/sign-in")}>
                            <span className={styles.linkIcon}><FiLogIn /></span>
                            <span>Sign In</span>
                        </Nav.Link>
                    )}
                </Nav>
            </Offcanvas.Body>
        </>
    );

    const renderCollections = () => (
        <>
            {renderHeader("Deals & Collections", "Explore curated product shelves", true)}
            <Offcanvas.Body className={styles.offcanvasBody}>
                <Nav className={styles.nav}>
                    <Nav.Link className={styles.backRow} onClick={goBack}>
                        <FiArrowLeft /> MAIN MENU
                    </Nav.Link>
                    <div className={styles.navLinkLine} />

                    {collectionItems.map((item) => (
                        <Nav.Link key={item.path} className={styles.navLink} onClick={() => goTo(item.path)}>
                            <span className={styles.linkIcon}>{item.icon}</span>
                            <span>{item.label}</span>
                        </Nav.Link>
                    ))}
                </Nav>
            </Offcanvas.Body>
        </>
    );

    const renderCategories = () => (
        <>
            {renderHeader("Shop by Category", "Browse every product category", true)}
            <Offcanvas.Body className={styles.offcanvasBody}>
                <Nav className={styles.nav}>
                    <Nav.Link className={styles.backRow} onClick={goBack}>
                        <FiArrowLeft /> MAIN MENU
                    </Nav.Link>
                    <div className={styles.navLinkLine} />

                    {categoryItems.map((item) => (
                        <Nav.Link
                            key={item.value}
                            className={styles.navLink}
                            onClick={() => goTo(`/category/${item.value}`)}
                        >
                            <span className={styles.linkIcon}>{item.icon || "•"}</span>
                            <span>{item.label}</span>
                            <FiChevronRight className={styles.chevron} />
                        </Nav.Link>
                    ))}
                </Nav>
            </Offcanvas.Body>
        </>
    );

    const renderHelp = () => (
        <>
            {renderHeader("Help & Information", "Everything you may need", true)}
            <Offcanvas.Body className={styles.offcanvasBody}>
                <Nav className={styles.nav}>
                    <Nav.Link className={styles.backRow} onClick={goBack}>
                        <FiArrowLeft /> MAIN MENU
                    </Nav.Link>
                    <div className={styles.navLinkLine} />

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/help")}>
                        <span className={styles.linkIcon}><FiHelpCircle /></span>
                        <span>Help Center</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/about-us")}>
                        <span className={styles.linkIcon}><FiInfo /></span>
                        <span>About Us</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/contact-us")}>
                        <span className={styles.linkIcon}><FiUser /></span>
                        <span>Contact Us</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/shipping-policy")}>
                        <span className={styles.linkIcon}><FiShoppingBag /></span>
                        <span>Shipping Policy</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/return-refund-policy")}>
                        <span className={styles.linkIcon}><FiTag /></span>
                        <span>Return & Refund</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/privacy-policy")}>
                        <span className={styles.linkIcon}><FiStar /></span>
                        <span>Privacy Policy</span>
                    </Nav.Link>

                    <Nav.Link className={styles.navLink} onClick={() => goTo("/terms-conditions")}>
                        <span className={styles.linkIcon}><FiInfo /></span>
                        <span>Terms & Conditions</span>
                    </Nav.Link>
                </Nav>
            </Offcanvas.Body>
        </>
    );

    return (
        <Offcanvas show={show} onHide={onHide} placement="start" className={styles.sidebar}>
            {activePanel === "main" && renderMainMenu()}
            {activePanel === "collections" && renderCollections()}
            {activePanel === "categories" && renderCategories()}
            {activePanel === "help" && renderHelp()}
        </Offcanvas>
    );
};

export default Sidebar;
